// Month calendar and list views of a team's services for the Roster page.
import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ServicePlan } from '@app/types';
import { AvatarStack, StatusPill, cx } from '@app/components/ui';
import { isPastDate, monthShort, time12, weekdayShort } from '@app/lib/format';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface FillState {
  confirmed: number;
  total: number;
  color: string;
  label: 'Done' | 'Full' | 'Pending' | 'Gaps' | 'Empty';
}

/** Full = everyone confirmed, Gaps = a decline or placeholder, Pending = waiting on replies. */
export function fillState(p: ServicePlan, now = new Date()): FillState {
  const total = p.team.length;
  const confirmed = p.team.filter((t) => t.status === 1).length;
  if (isPastDate(p.date, now)) return { confirmed, total, color: 'var(--t5)', label: 'Done' };
  if (!total) return { confirmed, total, color: 'var(--acc)', label: 'Empty' };
  if (confirmed === total) return { confirmed, total, color: 'var(--ok)', label: 'Full' };
  if (p.team.some((t) => t.status === 2 || t.isPlaceholder)) return { confirmed, total, color: 'var(--acc)', label: 'Gaps' };
  return { confirmed, total, color: 'var(--amb)', label: 'Pending' };
}

const chipTime = (d: string) => time12(d).replace(':00', '');
const describe = (p: ServicePlan) => {
  const f = fillState(p);
  return `${p.title}, ${weekdayShort(p.date)} ${monthShort(p.date)} ${new Date(p.date).getDate()}, ${time12(p.date)}, ${f.confirmed} of ${f.total} confirmed`;
};

export function RosterCalendar({
  month,
  onMonth,
  plans,
  selectedId,
  onSelect,
}: {
  month: Date;
  onMonth: (d: Date) => void;
  plans: ServicePlan[];
  selectedId: string | null;
  onSelect: (p: ServicePlan) => void;
}) {
  const today = new Date();
  const cells = useMemo(() => {
    const y = month.getFullYear();
    const m = month.getMonth();
    const first = new Date(y, m, 1).getDay();
    const dim = new Date(y, m + 1, 0).getDate();
    const out: Array<{ day: number | null; plans: ServicePlan[] }> = Array.from({ length: first }, () => ({ day: null, plans: [] }));
    for (let d = 1; d <= dim; d++) {
      out.push({
        day: d,
        plans: plans
          .filter((p) => {
            const pd = new Date(p.date);
            return pd.getFullYear() === y && pd.getMonth() === m && pd.getDate() === d;
          })
          .sort((a, b) => +new Date(a.date) - +new Date(b.date)),
      });
    }
    while (out.length % 7) out.push({ day: null, plans: [] });
    return out;
  }, [month, plans]);

  const title = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const shift = (n: number) => onMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div className="rounded-[24px] border border-line bg-card p-[18px]">
      <div className="flex flex-wrap gap-2.5 justify-between items-center px-1 pt-0.5 pb-3.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => shift(-1)} aria-label="Previous month" className="w-8 h-8 rounded-full bg-fill hover:bg-fill2 text-t2 flex items-center justify-center">
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <span className="text-[16px] font-semibold min-w-[140px] text-center" aria-live="polite">
            {title}
          </span>
          <button type="button" onClick={() => shift(1)} aria-label="Next month" className="w-8 h-8 rounded-full bg-fill hover:bg-fill2 text-t2 flex items-center justify-center">
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <span className="flex flex-wrap gap-3.5 text-[12px] text-t4">
          {[
            ['Full', 'var(--ok)'],
            ['Pending', 'var(--amb)'],
            ['Gaps', 'var(--acc)'],
            ['Past', 'var(--t5)'],
          ].map(([l, c]) => (
            <span key={l} className="flex items-center gap-1.5">
              <span className="w-[7px] h-[7px] rounded-full" style={{ background: c }} aria-hidden="true" />
              {l}
            </span>
          ))}
        </span>
      </div>
      <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {WEEKDAYS.map((w) => (
          <div key={w} className="wp-eyebrow !text-[11.5px] px-2 pt-1 pb-2" aria-hidden="true">
            {w}
          </div>
        ))}
        {cells.map((c, i) => {
          if (!c.day) return <div key={i} aria-hidden="true" />;
          const isToday = month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth() && c.day === today.getDate();
          const past = new Date(month.getFullYear(), month.getMonth(), c.day, 23, 59) < today;
          const hasSel = c.plans.some((p) => p.id === selectedId);
          return (
            <div
              key={i}
              className={cx('min-h-[92px] rounded-[14px] p-1.5 border border-line flex flex-col gap-1 min-w-0', hasSel && 'bg-acc-bg')}
            >
              <span
                className={cx(
                  'w-6 h-6 rounded-full font-mono text-[12px] font-bold flex items-center justify-center',
                  isToday ? 'bg-acc text-white' : past ? 'text-t5' : 'text-t2',
                )}
                aria-current={isToday ? 'date' : undefined}
              >
                {c.day}
              </span>
              {c.plans.map((p) => {
                const f = fillState(p);
                const on = p.id === selectedId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onSelect(p)}
                    aria-label={describe(p)}
                    aria-pressed={on}
                    title={p.title}
                    className={cx(
                      'flex items-center gap-[5px] w-full px-1.5 py-1 rounded-[8px] border text-t1 text-[11.5px] font-medium text-left whitespace-nowrap overflow-hidden',
                      on ? 'bg-card border-[rgba(208,48,69,.4)]' : 'bg-fill border-transparent hover:border-line2',
                    )}
                  >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: f.color }} aria-hidden="true" />
                    {chipTime(p.date)}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RosterList({ plans, selectedId, onSelect }: { plans: ServicePlan[]; selectedId: string | null; onSelect: (p: ServicePlan) => void }) {
  return (
    <div className="flex flex-col gap-2">
      {plans.map((p) => {
        const f = fillState(p);
        const on = p.id === selectedId;
        const status = f.label === 'Done' ? 'Done' : f.label === 'Full' ? 'Full' : 'Needs people';
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p)}
            aria-label={describe(p)}
            aria-pressed={on}
            className={cx(
              'flex flex-wrap items-center gap-4 w-full text-left px-[18px] py-3.5 rounded-[18px] border text-t1',
              on ? 'bg-acc-bg border-[rgba(208,48,69,.35)]' : 'bg-card border-line hover:border-line2',
            )}
          >
            <span className="w-[46px] text-center shrink-0">
              <span className="block text-[11px] font-semibold uppercase text-t4">{monthShort(p.date)}</span>
              <span className="block font-mono text-[20px] font-bold">{new Date(p.date).getDate()}</span>
            </span>
            <span className="flex-1 min-w-[160px]">
              <span className="block text-[15px] font-semibold">{p.title}</span>
              <span className="block text-[12.5px] text-t4 mt-0.5">
                {weekdayShort(p.date)} · {time12(p.date)} · {f.confirmed} of {f.total} confirmed
              </span>
            </span>
            <AvatarStack names={p.team.filter((t) => t.name).map((t) => t.name)} ring={on ? 'card' : 'bg'} />
            <StatusPill tone={f.label === 'Done' ? 'muted' : f.label === 'Full' ? 'ok' : 'amb'} className="min-w-[96px] justify-center">
              {status}
            </StatusPill>
          </button>
        );
      })}
    </div>
  );
}
